/**
 * Authentication client — replaces `supabase.auth` (GoTrue) with the NestJS
 * backend (`apps/api/src/auth`).
 *
 * WHY IT LOOKS LIKE `supabase.auth`
 * --------------------------------
 * There are 212 `supabase.auth.*` call sites across 85 files. Rewriting each one
 * into a bespoke shape would be a large, unreviewable diff and would have made
 * every call site subtly different. This exposes the same method names and the
 * same `{ data, error }` envelope, so migration is a rename:
 *
 *     auth.getSession()  →  auth.getSession()
 *
 * The one shape detail that matters: the app only ever reads `user.id`,
 * `user.email`, `session.user` and `session.access_token` (measured, not
 * assumed), so the stored user object stays deliberately small.
 *
 * WHAT CHANGED SEMANTICALLY
 * -------------------------
 * - Tokens live in `localStorage` under our own key. The Supabase client no
 *   longer manages a session at all; it forwards this token on every request
 *   which the API forwards as `request.jwt.claims`; that is what keeps RLS working.
 * - `getUser()` is answered from the stored session instead of calling a server.
 *   GoTrue's `/user` endpoint no longer exists, and a round trip to re-read a
 *   token we minted ourselves would only add latency.
 * - `getSession()` refreshes transparently when the access token is near expiry,
 *   so callers do not each need their own refresh logic.
 * - Password reset, sign-up confirmation and email change are **server-side**
 *   flows now. The link carries a single-use token hash; the page redeems it
 *   with `resetPassword()` / `confirmEmail()` rather than establishing a
 *   recovery session, so a reset works from a different browser or device.
 * - OAuth is a server-side redirect: `signInWithOAuth` navigates to the API,
 *   which owns the Google client secret, and the callback page adopts the
 *   session from the URL fragment (see `pages/AuthCallback.tsx`).
 */

import { syncSessionHint } from "./session-hint";

const STORAGE_KEY = "lyder-talk-auth:v1";

/** Refresh this many ms before expiry, so a request never races the deadline. */
const REFRESH_SKEW_MS = 60_000;

/**
 * Cross-tab mutex name for `navigator.locks`.
 *
 * A refresh token is single-use, so two tabs must never rotate the same one:
 * the server reads the second use as a replay and revokes the whole chain.
 */
const REFRESH_LOCK_NAME = "lyder-talk-auth-refresh";

export interface AuthUser {
  id: string;
  email: string | null;
  created_at?: string;
  /** Present only during a pending email change; the flow itself is unbuilt. */
  new_email?: string | null;
}

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  /** Milliseconds since epoch. */
  expires_at: number;
  user: AuthUser;
}

export interface AuthError {
  message: string;
  status?: number;
  /** Mirrors supabase-js, which sets e.g. `AuthSessionMissingError`. */
  name?: string;
}

interface Result<T> {
  data: T;
  error: AuthError | null;
}

type AuthEvent =
  | "INITIAL_SESSION"
  | "SIGNED_IN"
  | "SIGNED_OUT"
  | "TOKEN_REFRESHED"
  | "USER_UPDATED"
  // Kept in the union for compatibility. Recovery links no longer mint a
  // session — they are redeemed by `resetPassword()` — so this is never raised.
  | "PASSWORD_RECOVERY";

type Listener = (event: AuthEvent, session: AuthSession | null) => void;

/**
 * Result of one refresh attempt.
 *
 * `rejected` separates "the server says this refresh token is dead" from "we
 * could not ask". Only the first may clear the session; treating an unreachable
 * API as a rejection signs people out over a deploy.
 *
 * `race` is the server telling us another client rotated this same token a
 * moment ago. Nothing was revoked, so the fix is to adopt the winner's session —
 * never to sign out.
 */
interface RefreshOutcome {
  session: AuthSession | null;
  rejected: boolean;
  race: boolean;
}

function apiBaseUrl(): string | null {
  const value = import.meta.env.VITE_API_BASE_URL as string | undefined;
  return value ? value.replace(/\/+$/, "") : null;
}

function readStored(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    if (!parsed?.access_token || !parsed?.refresh_token || !parsed?.user?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(session: AuthSession | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode, quota). Auth then lives only for
    // the page's lifetime rather than throwing on every render.
  }
}

class AuthClient {
  private session: AuthSession | null = readStored();
  private listeners = new Set<Listener>();
  private refreshing: Promise<RefreshOutcome> | null = null;

  /* ------------------------------------------------------------------ tokens */

  /**
   * Current access token, synchronous.
   *
   * The Supabase client's fetch hook needs this on every request and cannot
   * await, so the session is held in memory and kept fresh by `getSession()`.
   */
  getAccessToken(): string | null {
    return this.session?.access_token ?? null;
  }

  /** The signed-in user's id, synchronous. Null when signed out. */
  getUserId(): string | null {
    return this.session?.user.id ?? null;
  }

  /** True when the access token is missing or about to expire. */
  private needsRefresh(): boolean {
    if (!this.session) return false;
    return this.session.expires_at - Date.now() < REFRESH_SKEW_MS;
  }

  /**
   * Adopt a rotation another tab already stored.
   *
   * Refresh tokens are single-use and the server treats a second use as a replay
   * — `auth.service.refresh` revokes **every** token the user holds, so two tabs
   * rotating the same token sign the learner out everywhere. A sibling's rotation
   * is visible in `localStorage`; adopting it is cheaper and safer than rotating
   * again.
   *
   * Only the same account is adopted. A different `user.id` means another tab
   * signed in as someone else, which must not silently swap this tab's identity.
   */
  private adoptSiblingRotation(): boolean {
    if (!this.session) return false;

    const stored = readStored();
    if (!stored) return false;
    if (stored.user.id !== this.session.user.id) return false;
    if (stored.access_token === this.session.access_token) return false;

    this.applySession(stored, "TOKEN_REFRESHED");
    return true;
  }

  /**
   * Run the refresh under a cross-tab lock where the Web Locks API exists.
   *
   * Adoption alone cannot fix the simultaneous case: two tabs that 401 in the
   * same tick (a laptop resuming) both read a session nobody has rotated yet.
   * The lock makes the second wait for the first, then adopt its result.
   */
  private async serialiseRefresh(run: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!locks?.request) return run();

    let started = false;
    const guarded = () => {
      started = true;
      return run();
    };

    try {
      return (await locks.request(REFRESH_LOCK_NAME, guarded)) as RefreshOutcome;
    } catch {
      // `runRefresh` never rejects, so a throw here is the lock API failing
      // before it called us. A browser without working locks must still be able
      // to refresh; if it did start, do not rotate a second time.
      return started ? { session: null, rejected: false, race: false } : run();
    }
  }

  private applySession(session: AuthSession | null, event: AuthEvent): void {
    this.session = session;
    writeStored(session);
    // Keep the cross-subdomain hint in step with the session, so the marketing
    // site can forward a signed-in visitor into the app. No-op off the app
    // origin (see `session-hint.ts`).
    syncSessionHint(Boolean(session));
    for (const listener of this.listeners) {
      try {
        listener(event, session);
      } catch (error) {
        console.error("auth listener threw", error);
      }
    }
  }

  /* ------------------------------------------------------------------ reads */

  async getSession(): Promise<Result<{ session: AuthSession | null }>> {
    if (!this.session) return { data: { session: null }, error: null };

    if (this.needsRefresh()) {
      const refreshed = await this.refresh();
      // A refresh that failed *without* the server rejecting the token (offline,
      // 5xx while a deploy rolls, timeout) must not turn into "signed out": the
      // stored session is still here, the UI stays signed in, and the transport's
      // 401 retry picks up a fresh token on the next request.
      return { data: { session: refreshed.data.session ?? this.session }, error: null };
    }

    return { data: { session: this.session }, error: null };
  }

  async getUser(): Promise<Result<{ user: AuthUser | null }>> {
    const { data } = await this.getSession();
    return { data: { user: data.session?.user ?? null }, error: null };
  }

  onAuthStateChange(callback: Listener): { data: { subscription: { unsubscribe: () => void } } } {
    this.listeners.add(callback);

    // Fire asynchronously, matching supabase-js: a listener registered during
    // render must not be called before the caller has its subscription back.
    queueMicrotask(() => {
      if (this.listeners.has(callback)) callback("INITIAL_SESSION", this.session);
    });

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners.delete(callback);
          },
        },
      },
    };
  }

  /* ----------------------------------------------------------------- writes */

  async signInWithPassword(credentials: { email: string; password: string }): Promise<
    Result<{ user: AuthUser | null; session: AuthSession | null }>
  > {
    return this.authenticate("/v1/auth/signin", credentials);
  }

  async signUp(params: {
    email: string;
    password: string;
    options?: {
      data?: { display_name?: string; displayName?: string };
      /** Where the confirmation link should land, when confirmation is required. */
      emailRedirectTo?: string;
    };
  }): Promise<Result<{ user: AuthUser | null; session: AuthSession | null }>> {
    return this.authenticate("/v1/auth/signup", {
      email: params.email,
      password: params.password,
      displayName: params.options?.data?.display_name ?? params.options?.data?.displayName,
      emailRedirectTo: params.options?.emailRedirectTo,
    });
  }

  async signOut(_options?: { scope?: string }): Promise<{ error: AuthError | null }> {
    const refreshToken = this.session?.refresh_token;
    // Clear locally first: a failed network call must not leave the user looking
    // signed in, and the refresh token is revoked server-side best-effort.
    this.applySession(null, "SIGNED_OUT");

    if (refreshToken && apiBaseUrl()) {
      try {
        await fetch(`${apiBaseUrl()}/v1/auth/signout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Offline sign-out still succeeds locally; the token expires on its own.
      }
    }

    return { error: null };
  }

  /** Rotate the refresh token and store the new pair. */
  async refresh(): Promise<Result<{ session: AuthSession | null }>> {
    if (!this.session?.refresh_token) {
      return { data: { session: null }, error: { message: "No refresh token" } };
    }

    // Collapse concurrent refreshes: several queries mounting at once would
    // otherwise rotate the same token repeatedly, and the second rotation is a
    // replay that revokes the whole chain.
    if (!this.refreshing) {
      this.refreshing = this.serialiseRefresh(async () => {
        // A sibling tab may have rotated while this one queued for the lock.
        if (this.adoptSiblingRotation()) {
          return { session: this.session, rejected: false, race: false };
        }
        return this.runRefresh();
      }).finally(() => {
        this.refreshing = null;
      });
    }

    const { session, rejected, race } = await this.refreshing;

    if (session) return { data: { session }, error: null };

    if (rejected) {
      // A rejected refresh means the chain is dead (revoked, replayed or
      // expired). Staying "signed in" with an unusable token would make every
      // request 401 with no explanation.
      this.applySession(null, "SIGNED_OUT");
      return { data: { session: null }, error: { message: "Session expired", status: 401 } };
    }

    // The server saw a concurrent rotation and revoked nothing. Adopt whatever
    // the winner stored; if it has not landed yet, keep the session and let the
    // next request retry — signing out here would be the race we are avoiding.
    if (race && this.adoptSiblingRotation()) {
      return { data: { session: this.session }, error: null };
    }

    return { data: { session: this.session }, error: { message: "Could not refresh your session" } };
  }

  /** One refresh round trip. Never decides the session is dead — `refresh()` does. */
  private async runRefresh(): Promise<RefreshOutcome> {
    const base = apiBaseUrl();
    if (!base) return { session: null, rejected: false, race: false };

    try {
      const response = await fetch(`${base}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: this.session!.refresh_token }),
      });

      if (!response.ok) {
        // 409 is the server telling us another client rotated this token a moment
        // ago and revoked nothing, so the session is still alive: adopt, never
        // sign out. 400/401/403 is the server refusing this token. 429 and 5xx
        // are the server being unavailable — a retry later can still succeed, so
        // keep the session rather than signing the learner out mid-deploy.
        const race = response.status === 409;
        const refused =
          response.status >= 400 && response.status < 500 && response.status !== 429 && !race;
        return { session: null, rejected: refused, race };
      }

      const body = await response.json();
      const next: AuthSession = {
        access_token: body.session.accessToken,
        refresh_token: body.session.refreshToken,
        expires_at: new Date(body.session.accessTokenExpiresAt).getTime(),
        user: {
          id: body.user.id,
          email: body.user.email ?? null,
        },
      };
      this.applySession(next, "TOKEN_REFRESHED");
      return { session: next, rejected: false, race: false };
    } catch {
      return { session: null, rejected: false, race: false };
    }
  }

  /**
   * A fresh access token for replaying a request that came back 401.
   *
   * The access token lives an hour and nothing refreshes it in the background, so
   * a tab left open past that boundary keeps sending a dead token — every
   * user-scoped read then 401s and the page looks like it has no data. The
   * transports (`db-client`, `api-client`) call this once on a 401 and retry.
   *
   * `getSession()` alone is not enough: it refreshes only within
   * `REFRESH_SKEW_MS` of expiry, and a token the server rejects can still look
   * valid locally (clock skew, rotated secret).
   *
   * Passing the token that failed lets a second, concurrent 401 ride the rotation
   * this one already did instead of triggering another (each refresh rotates the
   * refresh token, and replaying a rotated one revokes the chain).
   *
   * `expectedUserId` is the account the refused request was issued for. If the
   * session changed since then, the retry would run as a different learner, so it
   * is refused instead.
   */
  async refreshAccessToken(failedToken: string | null, expectedUserId: string | null = null): Promise<string | null> {
    if (expectedUserId && this.session?.user.id !== expectedUserId) return null;

    const current = this.session?.access_token ?? null;
    if (current && current !== failedToken) return current;

    const { data } = await this.refresh();

    // Re-check after the round trip: an adoption, a sign-out or a sign-in in
    // another tab could have changed the account while we waited.
    if (expectedUserId && data.session?.user.id !== expectedUserId) return null;
    return data.session?.access_token ?? null;
  }

  /** To keeps `auth.refreshSession()` call sites working. */
  async refreshSession(): Promise<Result<{ session: AuthSession | null }>> {
    return this.refresh();
  }

  /**
   * Update the signed-in user.
   *
   * `data` maps to the profile (display name), `password` changes the credential,
   * and `email` starts a change: the API emails a confirmation link to the *new*
   * address and leaves the account on the current one until it is confirmed.
   */
  async updateUser(
    attributes: { password?: string; email?: string; data?: { display_name?: string } },
    options?: { emailRedirectTo?: string },
  ): Promise<Result<{ user: AuthUser | null }>> {
    const base = apiBaseUrl();
    if (!base) return { data: { user: null }, error: { message: "API URL not configured" } };

    const { data: sessionData } = await this.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return { data: { user: null }, error: { message: "Not signed in" } };

    const response = await fetch(`${base}/v1/auth/user`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...attributes, emailRedirectTo: options?.emailRedirectTo }),
    });
    const body = await response.json().catch(() => null);

    if (!response.ok) {
      return { data: { user: null }, error: { message: body?.message ?? "Update failed", status: response.status } };
    }

    if (this.session) this.applySession({ ...this.session, user: body.user }, "USER_UPDATED");
    return { data: { user: body.user }, error: null };
  }

  /** Adopt an externally-issued token pair (used after OAuth redirects, if added). */
  async setSession(tokens: { access_token: string; refresh_token: string }): Promise<Result<{ session: AuthSession | null }>> {
    const base = apiBaseUrl();
    if (!base) return { data: { session: null }, error: { message: "API URL not configured" } };

    const response = await fetch(`${base}/v1/auth/me`, {
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    });
    if (!response.ok) return { data: { session: null }, error: { message: "Invalid session" } };

    const body = await response.json();
    const session: AuthSession = {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      // The backend is the authority on expiry; decode from the token's exp.
      expires_at: decodeExpiry(tokens.access_token) ?? Date.now() + 3_600_000,
      user: { id: body.profile.id, email: body.profile.email ?? null, created_at: body.profile.createdAt ?? undefined },
    };
    this.applySession(session, "SIGNED_IN");
    return { data: { session }, error: null };
  }

  /* ------------------------------------------------------- emailed auth links */

  /**
   * Ask the API to email a password-reset link.
   *
   * The endpoint answers the same whether or not the address is registered, so
   * callers must show the generic "check your inbox" state rather than infer
   * anything from success.
   */
  async resetPasswordForEmail(
    email: string,
    options?: { redirectTo?: string },
  ): Promise<{ data: null; error: AuthError | null }> {
    return this.postJson("/v1/auth/password/forgot", {
      email,
      redirectTo: options?.redirectTo,
    });
  }

  /** Redeem a reset link and set the new password. No session is required. */
  async resetPassword(input: { token: string; password: string }): Promise<{
    data: { reset?: boolean } | null;
    error: AuthError | null;
  }> {
    return this.postJson<{ reset?: boolean }>("/v1/auth/password/reset", input);
  }

  /** Confirm a sign-up or email-change link. No session is required. */
  async confirmEmail(token: string): Promise<{
    data: { confirmed?: boolean; email?: string | null; type?: "signup" | "email_change" } | null;
    error: AuthError | null;
  }> {
    return this.postJson<{ confirmed?: boolean; email?: string | null; type?: "signup" | "email_change" }>(
      "/v1/auth/email/confirm",
      { token },
    );
  }

  /** Send another sign-up confirmation link (same non-enumerating answer). */
  async resendConfirmation(
    email: string,
    options?: { redirectTo?: string },
  ): Promise<{ data: null; error: AuthError | null }> {
    return this.postJson("/v1/auth/email/resend", {
      email,
      redirectTo: options?.redirectTo,
    });
  }

  async exchangeCodeForSession(_code: string): Promise<{ data: null; error: AuthError }> {
    // Google's authorization code is exchanged by the API, never by the browser,
    // because the exchange needs GOOGLE_CLIENT_SECRET. The callback page adopts
    // the session the API hands back in the URL fragment instead.
    return { data: null, error: { message: "OAuth codes are exchanged by the API, not the client." } };
  }

  async verifyOtp(_params: unknown): Promise<{ data: null; error: AuthError }> {
    return { data: null, error: { message: "One-time codes are not available yet." } };
  }

  /**
   * Start an OAuth sign-in.
   *
   * Both legs of the flow are browser navigations, so this does not return a
   * session — it hands the page to the API, which hands it to Google, and the
   * callback page adopts the session on the way back. The promise resolves
   * before the navigation starts (with no error) so a caller's `finally` cannot
   * leave a spinner spinning in the way of the redirect.
   *
   * `redirectTo` must point at this app's `/auth/callback`; the API validates
   * its origin against first-party hosts before signing it into the OAuth state.
   */
  async signInWithOAuth(params: {
    provider: "google" | string;
    options?: {
      redirectTo?: string;
      queryParams?: Record<string, string>;
    };
  }): Promise<{ data: null; error: AuthError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "API URL not configured" } };

    if (params.provider !== "google") {
      return { data: null, error: { message: `${params.provider} sign-in is not available.` } };
    }

    if (typeof window === "undefined") {
      return { data: null, error: { message: "OAuth sign-in requires a browser" } };
    }

    const redirectTo = params.options?.redirectTo ?? `${window.location.origin}/auth/callback`;
    const url = new URL(`${base}/v1/auth/oauth/google/start`);
    url.searchParams.set("redirect_to", redirectTo);

    // The redirect is the result: the page leaves before anything else happens.
    window.location.assign(url.toString());
    return { data: null, error: null };
  }

  /* ----------------------------------------------------------------- private */

  /**
   * POST a small JSON body and normalise the response.
   *
   * The emailed-auth endpoints have nothing to do with the stored session, so
   * they share this instead of `authenticate()` — which would apply a session
   * that these routes never return.
   */
  private async postJson<T = null>(
    path: string,
    body: Record<string, unknown>,
  ): Promise<{ data: T | null; error: AuthError | null }> {
    const base = apiBaseUrl();
    if (!base) return { data: null, error: { message: "API URL not configured" } };

    try {
      const response = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          data: null,
          error: { message: payload?.message ?? "Request failed", status: response.status },
        };
      }

      return { data: (payload ?? null) as T | null, error: null };
    } catch (error) {
      return {
        data: null,
        error: { message: error instanceof Error ? error.message : "Network error" },
      };
    }
  }

  private async authenticate(
    path: string,
    body: Record<string, unknown>,
  ): Promise<Result<{ user: AuthUser | null; session: AuthSession | null }>> {
    const base = apiBaseUrl();
    if (!base) {
      return { data: { user: null, session: null }, error: { message: "API URL not configured" } };
    }

    try {
      const response = await fetch(`${base}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          data: { user: null, session: null },
          error: { message: payload?.message ?? "Sign-in failed", status: response.status },
        };
      }

      if (!payload?.session) {
        // Sign-up succeeded but requires email confirmation.
        return {
          data: { user: { id: payload.user.id, email: payload.user.email ?? null }, session: null },
          error: null,
        };
      }

      const session: AuthSession = {
        access_token: payload.session.accessToken,
        refresh_token: payload.session.refreshToken,
        expires_at: new Date(payload.session.accessTokenExpiresAt).getTime(),
        user: { id: payload.user.id, email: payload.user.email ?? null },
      };
      this.applySession(session, "SIGNED_IN");
      return { data: { user: session.user, session }, error: null };
    } catch (error) {
      return {
        data: { user: null, session: null },
        error: { message: error instanceof Error ? error.message : "Network error" },
      };
    }
  }
}

/** Read `exp` without verifying — the backend already verified it. */
function decodeExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export const auth = new AuthClient();
